import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { VehiclesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { SortOrder, VehicleFilters, VehicleOrder } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 20, onIndex = false, createable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<VehiclesQuery>(
    Query,
    { variables: { first: pageSize } }
  )
  const { pageInfo, setOpenNew, openShow, openNew, showShow, hideShow, resourceId } = useTable({ pageInfo: data?.vehicles.pageInfo, onIndex, basePath: '/vehicles' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<VehicleFilters, VehicleOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.vehicles.nodes = dedupe([
              ...previousData.vehicles.nodes,
              ...fetchMoreResult.vehicles.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Registration', sortField: 'registration', defaultDirection: SortOrder.Asc },
          { name: 'Warehouse' },
          { name: 'Hire Rate' },
          { name: 'Mileage Rate' },
        ]}
        rows={data.vehicles.nodes.map(vehicle => (
          {
            id: vehicle.id,
            columns: [
              vehicle.name,
              vehicle.registration,
              vehicle.warehouse?.name,
              vehicle.rateCents,
              vehicle.mileageCents
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />

    </>
  )
}
